import { useState } from "react";
import { getAuth } from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";

const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID as string;
const YEARLY_PRICE_ID = import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID as string;
const API_BASE = (import.meta.env.VITE_COMPILE_API_URL as string | undefined) ?? "/api";

export function UpgradeModal({ onClose, context }: { onClose: () => void; context?: "images" | "scans" }) {
    const { user } = useAuth();
    const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isYearly = billing === "yearly";
    const priceId = isYearly ? YEARLY_PRICE_ID : MONTHLY_PRICE_ID;
    const amount = isYearly ? "$60" : "$6";
    const period = isYearly ? "/year" : "/month";
    const savings = isYearly ? " — save $12" : null;

    const handleUpgrade = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const token = await getAuth().currentUser?.getIdToken();
            if (!token) { setError("Not signed in"); setLoading(false); return; }
            const res = await fetch(`${API_BASE}/stripe/create-checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ uid: user.uid, email: user.email, priceId }),
            });
            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                setError(data.error ?? "Failed to start checkout");
            }
        } catch {
            setError("Could not connect to server");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="confirm-modal-overlay" onClick={onClose}>
            <div className="confirm-modal upgrade-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="confirm-modal-title">Upgrade to SeeTex Pro</h3>
                <p className="confirm-modal-message">
                    {context === "images"
                        ? <>Your images are saved <strong>on this device only</strong>. Upgrade to Pro to sync them across all your devices via the cloud.</>
                        : <>You've used all <strong>5 free PDF scans</strong> this month. Upgrade to Pro for unlimited scans and priority compilation.</>
                    }
                </p>
                <div className="upgrade-modal-toggle">
                    <button
                        className={`upgrade-modal-toggle-btn${!isYearly ? " active" : ""}`}
                        onClick={() => setBilling("monthly")}
                    >
                        Monthly
                    </button>
                    <button
                        className={`upgrade-modal-toggle-btn${isYearly ? " active" : ""}`}
                        onClick={() => setBilling("yearly")}
                    >
                        Yearly
                    </button>
                </div>
                <div className="upgrade-modal-price">
                    <span className="upgrade-modal-amount">{amount}</span>
                    <span className="upgrade-modal-period">{period}</span>
                    {savings && <span className="upgrade-modal-savings">{savings}</span>}
                </div>
                <ul className="upgrade-modal-features">
                    <li>Unlimited PDF scans</li>
                    <li>Priority PDF compilation</li>
                    <li>Cloud image sync across devices</li>
                    <li>Cancel anytime</li>
                </ul>
                {error && <p className="upgrade-modal-error">{error}</p>}
                <div className="confirm-modal-actions">
                    <button className="confirm-modal-cancel" onClick={onClose} disabled={loading}>
                        Maybe later
                    </button>
                    <button
                        className="confirm-modal-confirm confirm-modal-confirm--primary"
                        onClick={handleUpgrade}
                        disabled={loading}
                    >
                        {loading ? "Redirecting…" : "Upgrade to Pro →"}
                    </button>
                </div>
            </div>
        </div>
    );
}
